import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DrilldownRows, fetchTransactions } from "../api";
import { DepositTransaction, DrilldownTarget, ExpenseTransaction, IncomeTransaction, PeriodSelection } from "../types";
import { fmt } from "../utils/format";
import { AttachmentButton } from "./AttachmentViewer";

interface Props {
  projectId: string;
  period: PeriodSelection;
  periodLabel: string;
  target: DrilldownTarget | null;
  breakdownTotal: number; // the amount shown on the row that was clicked
  onClose: () => void;
  onOpenFiles: (files: string[]) => void;
}

const head = (labels: string[], rightFrom: number) => (
  <TableHead>
    <TableRow>
      {labels.map((label, i) => (
        <TableCell key={label} align={i >= rightFrom ? "right" : "left"} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          {label}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

/**
 * The transactions behind one Income / Expense / Deposit row, for the same
 * period as the report. Rows and their amounts come from the backend; the
 * footer shows the breakdown figure the user clicked, so the two can be
 * compared at a glance.
 */
const TransactionDrilldownDialog: React.FC<Props> = ({
  projectId,
  period,
  periodLabel,
  target,
  breakdownTotal,
  onClose,
  onOpenFiles,
}) => {
  const [rows, setRows] = useState<DrilldownRows>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    fetchTransactions(projectId, period, target)
      .then((data) => !cancelled && setRows(data))
      .catch(() => !cancelled && setError("Failed to load transactions."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, period, target]);

  const footer = (colSpan: number, trailing: number) => (
    <TableFooter>
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ fontWeight: 700, color: "text.primary" }}>
          Total ({rows.length})
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}>
          {fmt(breakdownTotal)}
        </TableCell>
        {trailing > 0 && <TableCell colSpan={trailing} />}
      </TableRow>
    </TableFooter>
  );

  const renderExpenses = (list: ExpenseTransaction[]) => (
    <Table size="small">
      {head(["Date", "Description", "Category", "Sub-category", "Amount", "HST", "Total", "Returned", "Net", "Status", "Remarks", "Receipt"], 4)}
      <TableBody>
        {list.map((e) => (
          <TableRow key={e.id} hover>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{e.date}</TableCell>
            <TableCell sx={{ minWidth: 180 }}>{e.description || "—"}</TableCell>
            <TableCell>{e.category}</TableCell>
            <TableCell>{e.sub_category || "—"}</TableCell>
            <TableCell align="right">{fmt(e.amount)}</TableCell>
            <TableCell align="right">{fmt(e.hst)}</TableCell>
            <TableCell align="right">{fmt(e.total)}</TableCell>
            <TableCell align="right">{e.return_amount ? fmt(e.return_amount) : "—"}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {fmt(e.net_amount)}
            </TableCell>
            <TableCell align="right">
              <Chip size="small" label={e.status} color={e.status === "Paid" ? "success" : "default"} variant="outlined" />
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              {e.remarks || "—"}
            </TableCell>
            <TableCell align="right">
              {e.attachments.length ? (
                <AttachmentButton count={e.attachments.length} onClick={() => onOpenFiles(e.attachments)} />
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {footer(8, 3)}
    </Table>
  );

  const renderIncomes = (list: IncomeTransaction[]) => (
    <Table size="small">
      {head(["Date", "Source", "Description", "Payment Method", "Reference", "Amount", "Status", "Attachment"], 5)}
      <TableBody>
        {list.map((i) => (
          <TableRow key={i.id} hover>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{i.date}</TableCell>
            <TableCell>{i.source}</TableCell>
            <TableCell sx={{ minWidth: 160 }}>{i.description || "—"}</TableCell>
            <TableCell>{i.payment_method || "—"}</TableCell>
            <TableCell>{i.reference_number || i.booking_id || "—"}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {fmt(i.amount)}
            </TableCell>
            <TableCell align="right">
              <Chip size="small" label={i.status} color="success" variant="outlined" />
            </TableCell>
            <TableCell align="right">
              {i.attachments.length ? (
                <AttachmentButton count={i.attachments.length} onClick={() => onOpenFiles(i.attachments)} />
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {footer(5, 2)}
    </Table>
  );

  const renderDeposits = (list: DepositTransaction[]) => (
    <Table size="small">
      {head(["Date", "Partner", "Remarks", "Amount"], 3)}
      <TableBody>
        {list.map((d) => (
          <TableRow key={d.id} hover>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{d.date}</TableCell>
            <TableCell>{d.person}</TableCell>
            <TableCell>{d.remarks || "—"}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {fmt(d.amount)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {footer(3, 0)}
    </Table>
  );

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {target?.name}
        <Typography variant="body2" color="text.secondary">
          {periodLabel}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 3 }}>
            {error}
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            {target?.kind === "expense" && renderExpenses(rows as ExpenseTransaction[])}
            {target?.kind === "income" && renderIncomes(rows as IncomeTransaction[])}
            {target?.kind === "deposit" && renderDeposits(rows as DepositTransaction[])}
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDrilldownDialog;
